import React, { useState } from "react";
import axios from "axios";
import { CheckCircle2, Loader2, MapPin } from "lucide-react";
import { PUBLIC_API as API } from "../../lib/api";
import { useI18n } from "../../i18n";
import AddressAutocomplete from "../shared/AddressAutocomplete";
import { messageErreur } from "../../lib/messageErreur";

/**
 * Page de paiement ouverte depuis un lien VÉRIFIÉ (décision d'Avi,
 * 30/09/2026) : si la fiche n'a pas d'adresse complète, on propose —
 * facultatif — « Adresse pour votre reçu fiscal ». Enregistrée sur la fiche
 * par POST /api/pay/{token}/profile (refusé pour un lien fait sans code).
 *
 * Lien fait sans code (identity_verified === false) : seulement pour un
 * NOUVEAU donateur dont la fiche vient d'être créée par ce don — le serveur
 * le dit par ``adresse_recu_a_completer`` (un oui/non, rien de la fiche).
 * Jamais quand l'adresse est déjà complète ; ne bloque jamais le paiement.
 *
 * Props :
 *  - token      : jeton du lien de paiement
 *  - data       : réponse de GET /api/pay/{token}
 *  - onEnregistre() : recharge la page (l'adresse revient dans ``data``)
 */
export function adresseRecuAProposer(data) {
  if (!data) return false;
  if (data.identity_verified === false) return data.adresse_recu_a_completer === true;
  return ![data.donor_address, data.donor_postal_code, data.donor_city]
    .every((v) => String(v || "").trim());
}

export default function AdresseRecuFiscal({ token, data, onEnregistre }) {
  const { t } = useI18n();
  const [ouvert, setOuvert] = useState(false);
  const [adresse, setAdresse] = useState(data?.donor_address || "");
  const [codePostal, setCodePostal] = useState(data?.donor_postal_code || "");
  const [ville, setVille] = useState(data?.donor_city || "");
  const [etat, setEtat] = useState(""); // "" | envoi | fait | erreur
  const [erreur, setErreur] = useState("");

  const complet = adresse.trim() && codePostal.trim() && ville.trim();
  const inputCls = "w-full min-w-0 px-3 py-2.5 rounded-xl border border-stone-200 bg-white text-base focus:outline-none focus:ring-2 focus:ring-stone-900/10";

  const enregistrer = async () => {
    if (!complet || etat === "envoi") return;
    setEtat("envoi");
    setErreur("");
    try {
      await axios.post(`${API}/pay/${token}/profile`, {
        address: adresse.trim(), postal_code: codePostal.trim(), city: ville.trim(),
      });
      setEtat("fait");
      try { await onEnregistre?.(); } catch { /* non bloquant */ }
    } catch (e) {
      setEtat("erreur");
      setErreur(messageErreur(e, t("pay.address_error")));
    }
  };

  if (etat === "fait") {
    return (
      <div className="mt-5 p-4 rounded-2xl border border-emerald-300 bg-emerald-50 flex items-start gap-3"
        data-testid="adresse-recu-enregistree">
        <CheckCircle2 className="h-5 w-5 text-emerald-600 mt-0.5 shrink-0" />
        <p className="text-sm text-emerald-800">{t("pay.address_saved")}</p>
      </div>
    );
  }

  return (
    <div className="mt-5 p-4 rounded-2xl border border-stone-200 bg-white" data-testid="adresse-recu-fiscal">
      <div className="flex items-start gap-3">
        <MapPin className="h-5 w-5 text-chk-gold mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-chk-ink">
            {t("pay.address_title")}{" "}
            <span className="font-normal text-chk-ash">{t("pay.address_optional")}</span>
          </p>
          <p className="text-xs text-chk-ash mt-1 leading-relaxed">{t("pay.address_hint")}</p>
        </div>
        {!ouvert && (
          <button type="button" onClick={() => setOuvert(true)} data-testid="adresse-recu-ouvrir"
            className="shrink-0 px-4 py-2 rounded-full border border-chk-navy text-chk-navy text-xs font-semibold hover:bg-chk-navy hover:text-white transition-colors">
            {t("pay.address_add")}
          </button>
        )}
      </div>
      {ouvert && (
        <div className="mt-3 space-y-2">
          <AddressAutocomplete
            value={adresse}
            onChange={setAdresse}
            onSelect={({ address, postalCode, city }) => { setAdresse(address); setCodePostal(postalCode); setVille(city); }}
            placeholder={t("pay.address_street")}
            testid="adresse-recu-fiscal-rue"
            inputClassName={inputCls}
          />
          <div className="grid grid-cols-2 gap-2">
            <input value={codePostal} onChange={(e) => setCodePostal(e.target.value)}
              placeholder={t("pay.address_postal")} inputMode="numeric" autoComplete="postal-code"
              data-testid="adresse-recu-fiscal-cp" className={inputCls} />
            <input value={ville} onChange={(e) => setVille(e.target.value)}
              placeholder={t("pay.address_city")} autoComplete="address-level2"
              data-testid="adresse-recu-fiscal-ville" className={inputCls} />
          </div>
          {etat === "erreur" && (
            <p className="text-xs text-red-600" role="alert">{erreur}</p>
          )}
          <div className="flex items-center justify-end gap-3 pt-1">
            <button type="button" onClick={() => setOuvert(false)}
              className="text-xs text-chk-ash underline hover:text-chk-ink">
              {t("pay.address_later")}
            </button>
            <button type="button" onClick={enregistrer} disabled={!complet || etat === "envoi"}
              data-testid="adresse-recu-enregistrer"
              className="px-4 py-2 rounded-full bg-chk-navy text-white text-xs font-semibold disabled:opacity-40 inline-flex items-center gap-1.5">
              {etat === "envoi" && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              {t("pay.address_save")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
